export type ScheduleKind = 'daily' | 'weekly' | 'monthly' | 'custom';

export type ScheduleConfig = {
  kind: ScheduleKind;
  /** HH:mm (24h) */
  time: string;
  /** 0 = Sunday … 6 = Saturday */
  dayOfWeek: number;
  /** 1–28 */
  dayOfMonth: number;
  timezone: string;
  /** Only when kind === 'custom' */
  cronExpression: string;
};

export const DEFAULT_JOB_TIMEZONE = 'Europe/Paris';

export const TIMEZONE_OPTIONS = [
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem (IST/IDT)' },
];

export const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const DEFAULT_CONFIG: ScheduleConfig = {
  kind: 'daily',
  time: '02:00',
  dayOfWeek: 0,
  dayOfMonth: 1,
  timezone: DEFAULT_JOB_TIMEZONE,
  cronExpression: '',
};

function padTimePart(n: number): string {
  return String(n).padStart(2, '0');
}

function parseTimeParts(time: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return null;
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/** Build a 5-field cron from a friendly schedule (minute hour dom month dow). */
export function buildCronFromConfig(config: ScheduleConfig): string | null {
  if (config.kind === 'custom') {
    const cron = config.cronExpression?.trim();
    return cron || null;
  }

  const parts = parseTimeParts(config.time);
  if (!parts) return null;

  const { hour, minute } = parts;

  switch (config.kind) {
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekly':
      return `${minute} ${hour} * * ${config.dayOfWeek}`;
    case 'monthly':
      return `${minute} ${hour} ${config.dayOfMonth} * *`;
    default:
      return null;
  }
}

/** Best-effort parse of standard 5-field cron into friendly config. */
export function parseCronToConfig(
  cronExpression: string | null | undefined,
  timezone = DEFAULT_JOB_TIMEZONE
): ScheduleConfig {
  if (!cronExpression?.trim()) {
    return { ...DEFAULT_CONFIG, timezone };
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return {
      ...DEFAULT_CONFIG,
      kind: 'custom',
      cronExpression: cronExpression.trim(),
      timezone,
    };
  }

  const [min, hour, dom, month, dow] = parts;

  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour)) {
    return {
      ...DEFAULT_CONFIG,
      kind: 'custom',
      cronExpression: cronExpression.trim(),
      timezone,
    };
  }

  const time = `${padTimePart(parseInt(hour, 10))}:${padTimePart(parseInt(min, 10))}`;

  if (dom === '*' && month === '*' && dow === '*') {
    return { ...DEFAULT_CONFIG, kind: 'daily', time, timezone };
  }

  if (dom === '*' && month === '*' && /^\d+$/.test(dow)) {
    return {
      ...DEFAULT_CONFIG,
      kind: 'weekly',
      time,
      dayOfWeek: parseInt(dow, 10),
      timezone,
    };
  }

  if (/^\d+$/.test(dom) && month === '*' && dow === '*') {
    return {
      ...DEFAULT_CONFIG,
      kind: 'monthly',
      time,
      dayOfMonth: Math.min(Math.max(parseInt(dom, 10), 1), 28),
      timezone,
    };
  }

  return {
    ...DEFAULT_CONFIG,
    kind: 'custom',
    time,
    cronExpression: cronExpression.trim(),
    timezone,
  };
}

export function describeSchedule(
  enabled: boolean,
  cronExpression: string | null | undefined,
  timezone = DEFAULT_JOB_TIMEZONE
): string {
  if (!enabled || !cronExpression) {
    return 'Manual only — run from this page when needed';
  }

  const config = parseCronToConfig(cronExpression, timezone);
  const tzLabel = TIMEZONE_OPTIONS.find((t) => t.value === timezone)?.label || timezone;

  if (config.kind === 'custom') {
    return `Custom schedule (${cronExpression}) · ${tzLabel}`;
  }

  const [h, m] = config.time.split(':');
  const hour = parseInt(h, 10);
  const minute = parseInt(m, 10);
  const timeLabel = formatTime12h(hour, minute);

  switch (config.kind) {
    case 'daily':
      return `Every day at ${timeLabel} · ${tzLabel}`;
    case 'weekly': {
      const day =
        DAY_OF_WEEK_OPTIONS.find((d) => d.value === config.dayOfWeek)?.label || 'Sunday';
      return `Every ${day} at ${timeLabel} · ${tzLabel}`;
    }
    case 'monthly':
      return `Day ${config.dayOfMonth} of each month at ${timeLabel} · ${tzLabel}`;
    default:
      return cronExpression;
  }
}

function formatTime12h(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${padTimePart(minute)} ${period}`;
}

export function defaultScheduleConfig(
  cronExpression?: string | null,
  timezone?: string
): ScheduleConfig {
  return parseCronToConfig(cronExpression ?? '0 2 * * *', timezone || DEFAULT_JOB_TIMEZONE);
}

export function normalizeJobTimezone(timezone?: string | null): string {
  const tz = timezone || DEFAULT_JOB_TIMEZONE;
  return TIMEZONE_OPTIONS.some((o) => o.value === tz) ? tz : DEFAULT_JOB_TIMEZONE;
}

export function validateScheduleConfig(config: ScheduleConfig): string | null {
  if (config.kind === 'custom') {
    if (!config.cronExpression.trim()) return 'Enter a cron expression or choose another frequency';
    const parts = config.cronExpression.trim().split(/\s+/);
    if (parts.length !== 5) return 'Cron must have 5 fields (minute hour day month weekday)';
    return null;
  }
  if (!parseTimeParts(config.time)) return 'Enter a valid time (HH:mm)';
  if (config.kind === 'monthly' && (config.dayOfMonth < 1 || config.dayOfMonth > 28)) {
    return 'Day of month must be between 1 and 28';
  }
  return null;
}
